import type { LoaderFunctionArgs } from '~/types/route-args';
import type {
    GetProjectNextOpeningQuery,
    GetProjectNextOpeningQueryVariables,
} from 'generated/graphql';
import { GetProjectNextOpeningDocument } from 'generated/graphql';
import { PROJECT_REVAMP_BANNER } from '@modules/project/util/get-project-query';

export const NEXT_PROJECT_OPENING_API_ROUTE = '/remix-api/project/next-opening';

/** What a project page opens on: its banner, and the title over it. */
export interface NextProjectOpening {
    /** The project the opening belongs to, so a stale answer is never drawn under another project's name. */
    link: string;
    title: string;
    /** The rendition the hero paints, so the browser has it cached once the page turns. */
    imageSrc: string;
}

/**
 * The opening a project page paints, for the foot of the page before it.
 *
 * The foot grows the next project's banner into place and then turns the
 * page, and the turn is only invisible while the banner it grew is the banner
 * the next page paints: the same rendition on the same crop, with the same
 * title over it. Those come from the project itself rather than from its
 * listing tile, which carries a different crop of a different size.
 */
export const loader = async ({
    request,
    context,
}: LoaderFunctionArgs): Promise<NextProjectOpening | null> => {
    const link = new URL(request.url).searchParams.get('link');
    if (!link) throw new Response('Missing link', { status: 400 });

    const { data } = await context.client.query<
        GetProjectNextOpeningQuery,
        GetProjectNextOpeningQueryVariables
    >({
        query: GetProjectNextOpeningDocument,
        // The hero's own banner size, so the rendition here is the one the
        // next page paints and the turn never trades files.
        variables: {
            link,
            bannerWidthDesktop: PROJECT_REVAMP_BANNER.desktopWidth,
            bannerHeightDesktop: PROJECT_REVAMP_BANNER.desktopHeight,
        },
    });

    const node = data?.Directory?.Project;
    if (node?.__typename !== 'Project') return null;

    // The hero's own fallback: the social preview image when the banner has
    // no still, as on a video-only banner.
    const imageSrc =
        node.TopBanner?.BannerImage?.LibraryImageFile?.DetailPageBanner
            ?.Desktop ||
        node.FacebookPreviewImage ||
        '';
    if (!imageSrc) return null;

    return {
        link,
        title: node.ProjectHeroTitle || node.Title,
        imageSrc,
    };
};
